import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { HeartHandshake, Send, Loader2, Lock, Sparkles } from 'lucide-react';
import { PostCard } from '@/components/legacy/LegacyChannel';

const API_URL = window.location.origin;

export const LegacyPrayer = ({ user }) => {
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState('');
  const [privacy, setPrivacy] = useState('circle');
  const [posting, setPosting] = useState(false);
  const [view, setView] = useState('open'); // open | answered
  const [answering, setAnswering] = useState(null);
  const [answerNote, setAnswerNote] = useState('');

  const fetchPrayers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/legacy/prayers`);
      setPosts(res.data.posts);
    } catch (error) {
      console.error('Failed to load prayers:', error);
    }
  };

  useEffect(() => { fetchPrayers(); }, []);

  const handlePost = async () => {
    if (!content.trim()) { toast.error('Share your prayer request or encouragement'); return; }
    setPosting(true);
    try {
      await axios.post(`${API_URL}/api/legacy/prayers`, { content, privacy });
      toast.success(privacy === 'leaders' ? 'Shared privately with the leaders 🤍' : 'The circle is holding this with you 🤍');
      setContent('');
      setPrivacy('circle');
      fetchPrayers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };

  const handleAnswered = async (postId) => {
    try {
      await axios.post(`${API_URL}/api/legacy/prayers/${postId}/answered`, { note: answerNote });
      toast.success('Praise! Marked as answered 🙌');
      setAnswering(null);
      setAnswerNote('');
      fetchPrayers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const visible = posts.filter(p => (view === 'answered' ? p.answered : !p.answered));

  return (
    <div className="space-y-6" data-testid="legacy-prayer">
      <Card className="card-base border-terracotta/20 bg-gradient-to-br from-terracotta/5 to-transparent">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-1">
            <HeartHandshake className="h-5 w-5 text-terracotta" />
            <p className="text-xs uppercase tracking-widest text-terracotta">Prayer &amp; Encouragement</p>
          </div>
          <p className="font-heading text-xl text-forest">You do not have to carry it alone. Let the circle hold it with you.</p>
        </CardContent>
      </Card>

      {/* Composer */}
      <Card className="card-base">
        <CardContent className="p-4 space-y-3">
          <Textarea value={content} onChange={(e) => setContent(e.target.value)}
            placeholder="Share a prayer request, or speak encouragement over a sister..." rows={3} data-testid="prayer-content-input" />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-1.5">
              <button onClick={() => setPrivacy('circle')}
                className={`text-xs px-3 py-1.5 rounded-full border ${privacy === 'circle' ? 'bg-forest text-white border-forest' : 'border-sand text-gray-500'}`}
                data-testid="prayer-privacy-circle">
                Whole Circle
              </button>
              <button onClick={() => setPrivacy('leaders')}
                className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1 ${privacy === 'leaders' ? 'bg-forest text-white border-forest' : 'border-sand text-gray-500'}`}
                data-testid="prayer-privacy-leaders">
                <Lock className="h-3 w-3" /> Leaders Only
              </button>
            </div>
            <Button onClick={handlePost} disabled={posting} className="bg-forest hover:bg-forest/90 text-white" data-testid="prayer-submit-btn">
              {posting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />} Share
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* View toggle */}
      <div className="flex items-center gap-2">
        <button onClick={() => setView('open')} className={`text-xs px-3 py-1.5 rounded-full border ${view === 'open' ? 'bg-forest text-white border-forest' : 'border-sand text-gray-500'}`} data-testid="prayer-view-open">
          Being Held
        </button>
        <button onClick={() => setView('answered')} className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1 ${view === 'answered' ? 'bg-forest text-white border-forest' : 'border-sand text-gray-500'}`} data-testid="prayer-view-answered">
          <Sparkles className="h-3 w-3" /> Answered
        </button>
      </div>

      {/* Feed */}
      {visible.length > 0 ? (
        <div className="space-y-4">
          {visible.map(post => (
            <div key={post.id}>
              <div className="flex items-center gap-2 mb-1 ml-1">
                {post.privacy === 'leaders' && (
                  <Badge className="bg-gray-100 text-gray-500 text-xs flex items-center gap-1"><Lock className="h-3 w-3" /> Leaders Only</Badge>
                )}
                {post.answered && (
                  <Badge className="bg-champagne/40 text-forest text-xs flex items-center gap-1" data-testid={`prayer-answered-badge-${post.id}`}>
                    <Sparkles className="h-3 w-3" /> Answered {post.answered_at ? new Date(post.answered_at).toLocaleDateString() : ''}
                  </Badge>
                )}
              </div>
              <PostCard post={post} user={user} channel="prayer" onRefresh={fetchPrayers} />
              {post.answered && post.answered_note && (
                <p className="text-sm text-forest italic mt-1 ml-1">🙌 {post.answered_note}</p>
              )}
              {!post.answered && (post.author_id === user?.id) && (
                answering === post.id ? (
                  <div className="flex items-center gap-2 mt-2">
                    <Textarea value={answerNote} onChange={(e) => setAnswerNote(e.target.value)} rows={1}
                      placeholder="How was it answered? (optional)" className="flex-1" data-testid={`prayer-answer-note-${post.id}`} />
                    <Button size="sm" onClick={() => handleAnswered(post.id)} className="bg-forest text-white" data-testid={`prayer-answer-confirm-${post.id}`}>Confirm</Button>
                    <Button size="sm" variant="ghost" onClick={() => setAnswering(null)}>Cancel</Button>
                  </div>
                ) : (
                  <button onClick={() => setAnswering(post.id)} className="text-xs text-terracotta hover:underline mt-1 ml-1" data-testid={`prayer-mark-answered-${post.id}`}>
                    ✨ Mark as answered
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-sm text-gray-400 py-6">
          {view === 'answered' ? 'Answered prayers will be celebrated here.' : 'No requests right now. This space is here whenever you need it.'}
        </p>
      )}
    </div>
  );
};

export default LegacyPrayer;
